import {
  useCoolingRegimeProductGroup,
  useUpdateCoolingRegimeProductGroupTemperatures,
  useUpdateCoolingRegimeProductGroupPriorities,
  useDeleteCoolingRegimeProductGroup,
  useCreateCoolingRegimeProductGroup,
  useCoolingRegimeTemperatureOptions,
  useCoolingRegimePriorityOptions,
} from "../../hooks/useCoolingRegimeProductGroup";
import {
  EditableCoolingRegimeDetails,
  type TemperatureUpdates,
  type PriorityUpdates,
} from "../CoolingRegime/EditableCoolingRegimeDetails";
import { Box, Typography, IconButton, Dialog, DialogTitle, DialogContent, DialogActions, Button, FormControl, Select, MenuItem, TextField, Stack } from "@mui/material";
import { Add as AddIcon } from "@mui/icons-material";
import { useEffect, useState } from "react";
import { useFeatureFlag, Feature } from "../../hooks/useFeatureFlag";

export interface CoolingRegimeProductGroupDetailsProps {
  productGroupCode: string;
}

export function CoolingRegimeProductGroupDetails({
  productGroupCode,
}: CoolingRegimeProductGroupDetailsProps) {
  const { data, isLoading, error} =
    useCoolingRegimeProductGroup(productGroupCode);
  const updateTemperaturesMutation = useUpdateCoolingRegimeProductGroupTemperatures();
  const updatePrioritiesMutation = useUpdateCoolingRegimeProductGroupPriorities();
  const deleteMutation = useDeleteCoolingRegimeProductGroup();
  const createMutation = useCreateCoolingRegimeProductGroup();
  const { data: temperatureOptions } = useCoolingRegimeTemperatureOptions();
  const { data: priorityOptions } = useCoolingRegimePriorityOptions();
  const [createOpen, setCreateOpen] = useState(false);
  const [applicationCodeInput, setApplicationCodeInput] = useState<number>(1);
  const [rhValue, setRhValue] = useState<string>("N");
  const [shortTermTemp, setShortTermTemp] = useState<number | "">("");
  const [longTermTemp, setLongTermTemp] = useState<number | "">("");
  const [shortTermDispTemp, setShortTermDispTemp] = useState<number | "">("");
  const [longTermDispTemp, setLongTermDispTemp] = useState<number | "">("");
  const [shortTermPriority, setShortTermPriority] = useState<number | "">("");
  const [longTermPriority, setLongTermPriority] = useState<number | "">("");
  
  // Check permissions
  const canMutateCoolingRegimeProductGroup = useFeatureFlag(Feature.MutateCoolingRegimeGenus);
  const canMutateTemperature = useFeatureFlag(Feature.MutateCoolingRegimeTemperature);
  const canMutatePriority = useFeatureFlag(Feature.MutateCoolingRegimePriority);

  // Adjust defaults to first available option if id 1 is not present
  useEffect(() => {
    const tempIds = (temperatureOptions || [])
      .filter((t) => t.coolingRegimeTemperatureId != null)
      .map((t) => t.coolingRegimeTemperatureId!);
    if (tempIds.length > 0) {
      const defaultTemp = tempIds.includes(1) ? 1 : tempIds[0]!;
      if (shortTermTemp === "" || !tempIds.includes(Number(shortTermTemp))) setShortTermTemp(defaultTemp);
      if (longTermTemp === "" || !tempIds.includes(Number(longTermTemp))) setLongTermTemp(defaultTemp);
      if (shortTermDispTemp === "" || !tempIds.includes(Number(shortTermDispTemp))) setShortTermDispTemp(defaultTemp);
      if (longTermDispTemp === "" || !tempIds.includes(Number(longTermDispTemp))) setLongTermDispTemp(defaultTemp);
    }
  }, [temperatureOptions]);

  useEffect(() => {
    const prioIds = (priorityOptions || [])
      .filter((p) => p.coolingRegimePriorityId != null)
      .map((p) => p.coolingRegimePriorityId!);
    if (prioIds.length > 0) {
      const defaultPrio = prioIds.includes(1) ? 1 : prioIds[0]!;
      if (shortTermPriority === "" || !prioIds.includes(Number(shortTermPriority))) setShortTermPriority(defaultPrio);
      if (longTermPriority === "" || !prioIds.includes(Number(longTermPriority))) setLongTermPriority(defaultPrio);
    }
  }, [priorityOptions]);

  const handleSaveTemperature = async (
    field: keyof TemperatureUpdates,
    value: number | null,
  ) => {
    if (!data?.productGroupCode) return;

    const payload: any = {
      coolingRegimeProductGroupId: data.coolingRegimeProductGroupId,
      productGroupCode: data.productGroupCode,
      applicationCode: data.applicationCode,
      relativeHumidityIndication: data.relativeHumidityIndication,
      shortTermMainCoolingTemperatureId: data.coolingTemperatureCodeShortTerm,
      longTermMainCoolingTemperatureId: data.coolingTemperatureCodeLongTerm,
      shortTermDisplacementMainCoolingTemperatureId:
        data.coolingTemperatureCodeShortTermDisplacement,
      longTermDisplacementMainCoolingTemperatureId:
        data.coolingTemperatureCodeLongTermDisplacement,
    };

    // Update only the changed field
    if (field === "shortTermMain") {
      payload.shortTermMainCoolingTemperatureId = value;
    } else if (field === "longTermMain") {
      payload.longTermMainCoolingTemperatureId = value;
    } else if (field === "shortTermDisplacementMain") {
      payload.shortTermDisplacementMainCoolingTemperatureId = value;
    } else if (field === "longTermDisplacementMain") {
      payload.longTermDisplacementMainCoolingTemperatureId = value;
    }

    await updateTemperaturesMutation.mutateAsync(payload);
  };

  const handleSavePriority = async (
    field: keyof PriorityUpdates,
    value: number | null,
  ) => {
    if (!data?.coolingRegimeProductGroupId) return;

    const payload: any = {
      coolingRegimeProductGroupId: data.coolingRegimeProductGroupId,
      shortTermCoolingPriorityId: data.coolingPriorityCodeShortTerm,
      longTermCoolingPriorityId: data.coolingPriorityCodeLongTerm,
    };

    // Update only the changed field
    if (field === "shortTerm") {
      payload.shortTermCoolingPriorityId = value;
    } else if (field === "longTerm") {
      payload.longTermCoolingPriorityId = value;
    }

    await updatePrioritiesMutation.mutateAsync(payload);
  };

  const handleSaveRelativeHumidity = async (value: 'J' | 'N' | null) => {
    if (!data?.coolingRegimeProductGroupId) return;
    await updatePrioritiesMutation.mutateAsync({
      coolingRegimeProductGroupId: data.coolingRegimeProductGroupId,
      relativeHumidityIndication: value ?? null,
    } as any);
  };

  const handleDelete = async () => {
    if (!data?.coolingRegimeProductGroupId) return;
    const confirmed = window.confirm(
      "Weet je zeker dat je dit koelregime wilt verwijderen?",
    );
    if (!confirmed) return;
    await deleteMutation.mutateAsync(data.coolingRegimeProductGroupId);
  };

  // Map data to the format expected by EditableCoolingRegimeDetails
  const mappedData = data
    ? {
        code: data.productGroupCode,
        applicationCode: data.applicationCode,
        relativeHumidityIndication: data.relativeHumidityIndication,
        coolingTemperatureCodeShortTerm: data.coolingTemperatureCodeShortTerm,
        coolingTemperatureCodeLongTerm: data.coolingTemperatureCodeLongTerm,
        coolingTemperatureCodeShortTermDisplacement:
          data.coolingTemperatureCodeShortTermDisplacement,
        coolingTemperatureCodeLongTermDisplacement:
          data.coolingTemperatureCodeLongTermDisplacement,
        temperatureShortTerm: data.temperatureShortTerm,
        temperatureLongTerm: data.temperatureLongTerm,
        temperatureShortTermDisplacement: data.temperatureShortTermDisplacement,
        temperatureLongTermDisplacement: data.temperatureLongTermDisplacement,
        coolingPriorityCodeShortTerm: data.coolingPriorityCodeShortTerm,
        coolingPriorityCodeLongTerm: data.coolingPriorityCodeLongTerm,
        priorityShortTerm: data.priorityShortTerm,
        priorityLongTerm: data.priorityLongTerm,
        created: data.created,
        createdBy: data.createdBy,
        lastModified: data.lastModified,
        lastModifiedBy: data.lastModifiedBy,
      }
    : null;

  // Show empty state with add button when no record or id is 0
  if (((!data) || data.coolingRegimeProductGroupId === 0) && !isLoading && !error) {
    return (
      <Box p={2} display="flex" alignItems="center" justifyContent="space-between">
        <Typography>Geen koelregime gevonden</Typography>
        {canMutateCoolingRegimeProductGroup && (
          <IconButton
            aria-label="Nieuw koelregime"
            color="primary"
            onClick={() => setCreateOpen(true)}
            sx={{ bgcolor: "primary.main", color: "white", "&:hover": { bgcolor: "primary.dark" } }}
          >
            <AddIcon />
          </IconButton>
        )}

        <Dialog open={createOpen} onClose={() => setCreateOpen(false)}>
          <DialogTitle>Koelregime (productgroep) aanmaken</DialogTitle>
          <DialogContent sx={{ minWidth: 400 }}>
            <Typography variant="body2" sx={{ mb: 2 }}>
              Basisinstellingen met standaarden. Pas aan indien nodig.
            </Typography>
            <TextField
              fullWidth
              size="small"
              type="number"
              label="Toepassing"
              value={applicationCodeInput}
              onChange={(e) => {
                const v = Number(e.target.value);
                setApplicationCodeInput(Number.isNaN(v) ? 1 : v);
              }}
              sx={{ mb: 2 }}
            />
            <FormControl fullWidth size="small">
              <Select
                value={rhValue}
                onChange={(e) => setRhValue(String(e.target.value))}
                displayEmpty
                renderValue={(val) => (String(val).toUpperCase() === "J" ? "Ja" : "Nee")}
              >
                <MenuItem value="J">Ja</MenuItem>
                <MenuItem value="N">Nee</MenuItem>
              </Select>
            </FormControl>

            <Stack spacing={2} sx={{ mt: 2 }}>
              <Typography variant="subtitle2">Koeltemperatuur &lt; 24u</Typography>
              <FormControl fullWidth size="small">
                <Select
                  value={shortTermTemp}
                  onChange={(e) => setShortTermTemp(Number(e.target.value))}
                  displayEmpty
                  renderValue={(val) => {
                    const v = String(val ?? "");
                    if (!v) return "Selecteer temperatuur";
                    const match = temperatureOptions?.find(
                      (t) => t.coolingRegimeTemperatureId === Number(v),
                    );
                    return match?.description ?? v;
                  }}
                >
                  <MenuItem value="">
                    <em>Selecteer temperatuur</em>
                  </MenuItem>
                  {temperatureOptions
                    ?.filter((t) => t.coolingRegimeTemperatureId != null)
                    .map((t) => (
                      <MenuItem key={t.coolingRegimeTemperatureId!} value={t.coolingRegimeTemperatureId!}>
                        {t.description}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>

              <Typography variant="subtitle2">Koeltemperatuur &gt; 24u</Typography>
              <FormControl fullWidth size="small">
                <Select
                  value={longTermTemp}
                  onChange={(e) => setLongTermTemp(Number(e.target.value))}
                  displayEmpty
                  renderValue={(val) => {
                    const v = String(val ?? "");
                    if (!v) return "Selecteer temperatuur";
                    const match = temperatureOptions?.find(
                      (t) => t.coolingRegimeTemperatureId === Number(v),
                    );
                    return match?.description ?? v;
                  }}
                >
                  <MenuItem value="">
                    <em>Selecteer temperatuur</em>
                  </MenuItem>
                  {temperatureOptions
                    ?.filter((t) => t.coolingRegimeTemperatureId != null)
                    .map((t) => (
                      <MenuItem key={t.coolingRegimeTemperatureId!} value={t.coolingRegimeTemperatureId!}>
                        {t.description}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>

              <Typography variant="subtitle2">Uitwijk &lt; 24u</Typography>
              <FormControl fullWidth size="small">
                <Select
                  value={shortTermDispTemp}
                  onChange={(e) => setShortTermDispTemp(Number(e.target.value))}
                  displayEmpty
                  renderValue={(val) => {
                    const v = String(val ?? "");
                    if (!v) return "Selecteer temperatuur";
                    const match = temperatureOptions?.find(
                      (t) => t.coolingRegimeTemperatureId === Number(v),
                    );
                    return match?.description ?? v;
                  }}
                >
                  <MenuItem value="">
                    <em>Selecteer temperatuur</em>
                  </MenuItem>
                  {temperatureOptions
                    ?.filter((t) => t.coolingRegimeTemperatureId != null)
                    .map((t) => (
                      <MenuItem key={t.coolingRegimeTemperatureId!} value={t.coolingRegimeTemperatureId!}>
                        {t.description}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>

              <Typography variant="subtitle2">Uitwijk &gt; 24u</Typography>
              <FormControl fullWidth size="small">
                <Select
                  value={longTermDispTemp}
                  onChange={(e) => setLongTermDispTemp(Number(e.target.value))}
                  displayEmpty
                  renderValue={(val) => {
                    const v = String(val ?? "");
                    if (!v) return "Selecteer temperatuur";
                    const match = temperatureOptions?.find(
                      (t) => t.coolingRegimeTemperatureId === Number(v),
                    );
                    return match?.description ?? v;
                  }}
                >
                  <MenuItem value="">
                    <em>Selecteer temperatuur</em>
                  </MenuItem>
                  {temperatureOptions
                    ?.filter((t) => t.coolingRegimeTemperatureId != null)
                    .map((t) => (
                      <MenuItem key={t.coolingRegimeTemperatureId!} value={t.coolingRegimeTemperatureId!}>
                        {t.description}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>

              <Typography variant="subtitle2">Prioriteit &lt; 24u</Typography>
              <FormControl fullWidth size="small">
                <Select
                  value={shortTermPriority}
                  onChange={(e) => setShortTermPriority(Number(e.target.value))}
                  displayEmpty
                  renderValue={(val) => {
                    const v = String(val ?? "");
                    if (!v) return "Selecteer prioriteit";
                    const match = priorityOptions?.find(
                      (p) => p.coolingRegimePriorityId === Number(v),
                    );
                    return match?.description ?? v;
                  }}
                >
                  <MenuItem value="">
                    <em>Selecteer prioriteit</em>
                  </MenuItem>
                  {priorityOptions?.map((p) => (
                    <MenuItem key={p.coolingRegimePriorityId ?? 0} value={p.coolingRegimePriorityId ?? ""}>
                      {p.description}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Typography variant="subtitle2">Prioriteit &gt; 24u</Typography>
              <FormControl fullWidth size="small">
                <Select
                  value={longTermPriority}
                  onChange={(e) => setLongTermPriority(Number(e.target.value))}
                  displayEmpty
                  renderValue={(val) => {
                    const v = String(val ?? "");
                    if (!v) return "Selecteer prioriteit";
                    const match = priorityOptions?.find(
                      (p) => p.coolingRegimePriorityId === Number(v),
                    );
                    return match?.description ?? v;
                  }}
                >
                  <MenuItem value="">
                    <em>Selecteer prioriteit</em>
                  </MenuItem>
                  {priorityOptions?.map((p) => (
                    <MenuItem key={p.coolingRegimePriorityId ?? 0} value={p.coolingRegimePriorityId ?? ""}>
                      {p.description}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setCreateOpen(false)}>Annuleren</Button>
            <Button
              variant="contained"
              onClick={async () => {
                await createMutation.mutateAsync({
                  productGroupCode: parseInt(productGroupCode),
                  applicationCode: applicationCodeInput,
                  relativeHumidityIndication: rhValue,
                  // Explicitly include template fields with defaults set to 1
                  coolingTemperatureCodeShortTerm: shortTermTemp === "" ? null : shortTermTemp,
                  coolingTemperatureCodeLongTerm: longTermTemp === "" ? null : longTermTemp,
                  coolingTemperatureCodeShortTermDisplacement: shortTermDispTemp === "" ? null : shortTermDispTemp,
                  coolingTemperatureCodeLongTermDisplacement: longTermDispTemp === "" ? null : longTermDispTemp,
                  coolingPriorityCodeShortTerm: shortTermPriority === "" ? null : shortTermPriority,
                  coolingPriorityCodeLongTerm: longTermPriority === "" ? null : longTermPriority,
                });
                setCreateOpen(false);
              }}
              disabled={createMutation.isPending}
            >
              Opslaan
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    );
  }

  return (
    <EditableCoolingRegimeDetails
      data={mappedData}
      isLoading={isLoading}
      error={error}
      onSaveTemperature={handleSaveTemperature}
      onSavePriority={handleSavePriority}
      onSaveRelativeHumidity={handleSaveRelativeHumidity}
      isSaving={
        updateTemperaturesMutation.isPending ||
        updatePrioritiesMutation.isPending
      }
      codeLabel="Productgroep code"
      showPriorities={true}
      onDelete={canMutateCoolingRegimeProductGroup ? handleDelete : undefined}
      isDeleting={deleteMutation.isPending}
      canEdit={canMutateCoolingRegimeProductGroup || canMutateTemperature || canMutatePriority}
    />
  );
}
